import React, { FC, useState } from "react";
import { H3, H4, Span } from "../Text/Text";
import Icon from "../Icon/Icon";
import { ClientTypeE, IconColorE, IconSizeE } from "../../models/enums";
import { isMobile, isMobileOrTablet } from "../../utils/helpers/mediaQueries";
import { BASE_URL } from "../../api/config";
import { BroadcasterT, RecepientT } from "../../models/models";
import Tooltip from "../Tooltip/Tooltip";
import AsyncImage from "../AsyncImage/AsyncImage";

import styles from "./participantsInfo.module.scss";
import getImageLink from "../../utils/helpers/getImageLink";
import { useAppSelector } from "../../store/hooks";
import { selectShareToken } from "../../store/selectors/appSelectors";
import {
  DEFAULT_ADVERTISER_AGENCY_ID,
  DEFAULT_NO_ADVERTISER_AGENCY_ID,
  JUMBO_DELIVERY_ENG_USER_ID,
  JUMBO_DELIVERY_HEB_USER_ID,
} from "../../utils/constants";

/**
 * spec-41 D4 — `"deliveryService"` renders **Media company + Destinations only**: no Participants
 * list (advertising agency / post-production), no Production company. A delivery-service customer
 * is buying delivery, not production, so those blocks are not theirs to see.
 *
 * 🔴 Additive on purpose. This component is shared by the agency sidebar (`SidebarParticipants`)
 * and by the mobile Participants tab in `ViewVideoProject`; `"default"` must stay byte-identical
 * for every other role (spec-41 R6). Add sections to a variant, never restructure the default.
 */
export type ParticipantsInfoVariantT = "default" | "deliveryService";

type ParticipantsInfoPropTypes = {
  participants: RecepientT[];
  broadcasters: BroadcasterT[];
  productionCompanyData: {
    name: string;
    id: number;
  };
  mediaCompanyData?: {
    name: string | null;
    id: number | null;
  };
  variant?: ParticipantsInfoVariantT;
};

const ParticipantsInfo: FC<ParticipantsInfoPropTypes> = ({
  participants,
  broadcasters,
  productionCompanyData,
  mediaCompanyData,
  variant = "default",
}) => {
  const [expandedItemIndex, setExpandedItemIndex] = useState(-1);
  const shareToken = useAppSelector(selectShareToken);
  // spec-41 D4 — everything outside Media company + Destinations hangs off this.
  const isDefaultVariant = variant === "default";

  const toggleExpandedItem = (index: number) => {
    if (index === expandedItemIndex) {
      setExpandedItemIndex(-1);
    } else {
      setExpandedItemIndex(index);
    }
  };

  return (
    <div className={styles.wrapper}>
      {isDefaultVariant && !!participants && <H3>Participants</H3>}
      <ul className={styles.participantList}>
        {isDefaultVariant &&
          participants
          ?.filter(
            (value, index, self) =>
              index === self.findIndex((p) => p.ClientId === value.ClientId)
          )
          .map((participant, index) => (
            <li key={participant.UserId}>
              <h4>{ClientTypeE[participant.ClientType]}:</h4>
              <div>
                <div
                  className={styles.participantHeader}
                  onClick={() => toggleExpandedItem(index)}
                >
                  <div className={styles.photoNameWrapper}>
                    <AsyncImage
                      link={getImageLink({
                        id: participant.ClientId,
                        shareToken,
                      })}
                      addClassName={styles.participantPhoto}
                    />
                    <p className={styles.participantName}>
                      {participant.ClientName}
                    </p>
                  </div>
                  {participant.UserId !== JUMBO_DELIVERY_ENG_USER_ID &&
                    participant.UserId !== JUMBO_DELIVERY_HEB_USER_ID && (
                      <Icon
                        name="moreArrow"
                        color={
                          isMobileOrTablet()
                            ? IconColorE.BLACK
                            : IconColorE.WHITE
                        }
                        size={IconSizeE.S}
                        className={
                          expandedItemIndex === index
                            ? styles.selectedMoreArrow
                            : ""
                        }
                      />
                    )}
                </div>
                <div className={styles.participantInfoListWrapper}>
                  {expandedItemIndex === index &&
                    participant.UserId !== JUMBO_DELIVERY_ENG_USER_ID &&
                    participant.UserId !== JUMBO_DELIVERY_HEB_USER_ID && (
                      <ul className={styles.participantInfoList}>
                        {participants
                          .filter(
                            (item) => item.ClientId === participant.ClientId
                          )
                          .map((user, index) => (
                            <li key={participant.UserFullName + index}>
                              <p>{user.UserFullName}</p>
                              <div className={styles.userActions}>
                                {!!user.Email && (
                                  <a href={`mailto:${user.Email}`}>
                                    <Icon
                                      name="envelope"
                                      color={
                                        isMobile()
                                          ? IconColorE.GRAY
                                          : IconColorE.LIGHT_GRAY
                                      }
                                    />
                                  </a>
                                )}
                                {!!user.Phone && (
                                  <a href={`tel:${user.Phone}`}>
                                    <Icon
                                      name="phone"
                                      color={
                                        isMobile()
                                          ? IconColorE.GRAY
                                          : IconColorE.LIGHT_GRAY
                                      }
                                    />
                                  </a>
                                )}
                                {!!user.Phone && (
                                  <a
                                    href={`https://wa.me/${user.Phone}`}
                                    target="_blank"
                                  >
                                    <Icon
                                      name="whatsapp"
                                      color={
                                        isMobile()
                                          ? IconColorE.GRAY
                                          : IconColorE.LIGHT_GRAY
                                      }
                                    />
                                  </a>
                                )}
                              </div>
                            </li>
                          ))}
                      </ul>
                    )}
                </div>
              </div>
            </li>
          ))}
        <li>
          <h4>
            <Span>Media company</Span>:
          </h4>
          <div className={styles.prodCompany}>
            <div className={styles.participantHeader}>
              <div className={styles.photoNameWrapper}>
                {mediaCompanyData?.name ? (
                  <>
                    <AsyncImage
                      link={getImageLink({
                        id: mediaCompanyData?.id,
                        shareToken,
                      })}
                      addClassName={styles.participantPhoto}
                    />
                    <p className={styles.participantName}>
                      {mediaCompanyData?.name}
                    </p>
                  </>
                ) : (
                  <p className={styles.participantName}>
                    <Span>Self Media Buy</Span>
                  </p>
                )}
              </div>
            </div>
          </div>
        </li>
        {isDefaultVariant && (
          <li>
            <h4>
              <Span>Production company</Span>:
            </h4>
            <div className={styles.prodCompany}>
              <div className={styles.participantHeader}>
                <div className={styles.photoNameWrapper}>
                  <AsyncImage
                    link={getImageLink({
                      id: productionCompanyData?.id,
                      shareToken,
                    })}
                    addClassName={styles.participantPhoto}
                  />
                  <p className={styles.participantName}>
                    {productionCompanyData?.name}
                  </p>
                </div>
              </div>
            </div>
          </li>
        )}
      </ul>
      {!!broadcasters && <H4>Destinations:</H4>}
      <ul className={styles.destinationList}>
        {broadcasters?.map((item, index) => (
          <Tooltip text={item.Name} key={index}>
            <li>
              <AsyncImage
                link={getImageLink({ id: item.Id, shareToken })}
              />
            </li>
          </Tooltip>
        ))}
      </ul>
    </div>
  );
};

export default ParticipantsInfo;
